/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      // The entry the page loads on has no history state, and handleHistoryChange
      // ignores a null state - so going Back to it used to change the URL while
      // leaving the filtered results and ticked boxes on screen. Give that first
      // entry its own params so the existing guard can act on it. Fixing it here
      // rather than by loosening the guard keeps unrelated popstates from
      // resetting the filters.
      if (window.history.state === null) {
        window.history.replaceState(
          { searchParams: window.location.search.replace(/^\?/, '') },
          ''
        );
      }
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters?.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters?.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters?.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters?.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy?.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        // Widened past lg with the mobile drawer open (a rotated tablet, a
        // resized window): close it properly first. Otherwise its overlay and
        // scroll lock stayed on over the sidebar layout and blocked the page.
        if (this.getAttribute('role') === 'dialog' && this.getAttribute('aria-hidden') === 'false') {
          this.close();
          this.classList.remove('is-sort-mode');
          document.documentElement.classList.remove('drawer-open');
        }
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.removeAttribute('open');
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      // Acknowledge the interaction immediately. The debounce below is worth
      // keeping - it batches rapid multi-select into a single request - but on
      // its own it leaves the grid untouched for half a second, which reads as
      // the click having been ignored.
      this.results?.classList.add('is-loading');

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        // Normalise an inverted price range. A min above the max returns zero
        // products with nothing explaining why. price-range.js is meant to clamp
        // this, but its handleInput branches on `evt.detail !== undefined` and an
        // InputEvent's detail is 0 - never undefined - so the clamp always lands
        // on its own 500ms timer, racing this one. Swapping here means whichever
        // timer wins, the request carries a sane range, and the re-render echoes
        // the corrected values back into the inputs.
        const priceMin = searchParams.get('filter.v.price.gte');
        const priceMax = searchParams.get('filter.v.price.lte');
        if (priceMin && priceMax && Number(priceMin) > Number(priceMax)) {
          searchParams.set('filter.v.price.gte', priceMax);
          searchParams.set('filter.v.price.lte', priceMin);
        }

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      // closest, not tagName: a chip's label is a <span> and its X an <svg>, so
      // a click on either used to fall through and reload the whole page.
      const link = evt.target.closest('a');
      if (!link || !this.activeFilters.contains(link)) return;
      evt.preventDefault();
      this.applyFilters(new URL(link.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      let thisController;
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        // On an oversized collection the results come from the search endpoint,
        // so keep requesting them from there while a query is active. The URL
        // stays on the collection - see updateURL().
        const searchScope = document.querySelector('collection-search-scope');
        const isScopedSearch = searchScope && new URLSearchParams(searchParams).get('q');

        // Re-assert the collection's scope on every request. "Clear all" builds
        // a url without it, which would otherwise widen the results to products
        // the collection being browsed doesn't contain.
        const requestParams = isScopedSearch ? searchScope.withScope(searchParams) : searchParams;
        const basePath = isScopedSearch ? searchScope.dataset.searchUrl : window.location.pathname;

        let fetchUrl = `${basePath}?${requestParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();
        thisController = this.applyFiltersFetchAbortController;

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // The toolbar's result count lives outside both swapped containers, so
          // like the button above it has to be carried across by hand. Writing
          // textContent into the persistent node is also what makes its
          // role="status" announce - swapping the node out would say nothing.
          const newCount = tmpl.content.querySelector('[data-products-count]');
          const liveCount = document.querySelector('[data-products-count]');
          if (newCount && liveCount) {
            liveCount.textContent = newCount.textContent.trim();
          }

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) {
            // The address bar shows the shopper's choices; the history entry
            // keeps the full parameter set so back/forward can replay it.
            FacetFilters.updateURL(
              requestParams,
              isScopedSearch ? searchScope.visibleParams(requestParams) : undefined
            );
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML. Without
          // preventScroll this dragged the page back down to the checkbox the
          // shopper had clicked, undoing the scroll to "Alla produkter" above.
          if (activeElementId) document.getElementById(activeElementId)?.focus({ preventScroll: true });

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });

          // Scroll back up to "Alla produkter", clear of the header and the
          // pinned toolbar. After the broadcast, not before: listeners re-lay the
          // page above the heading (the device filter shrinks by ~86px), and a
          // target measured first left the heading under the pinned bar.
          customElements.get('products-toolbar')?.scrollToResults();
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        // An aborted request lands here while its replacement is still in flight.
        // Clearing the loading state unconditionally would drop the spinner off a
        // request that has not finished, so only the newest call clears it.
        if (thisController === undefined
            || this.applyFiltersFetchAbortController === thisController) {
          this.results.classList.remove('is-loading');
        }
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams, visibleParams) {
      const shown = visibleParams === undefined ? searchParams : visibleParams;

      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${shown && '?'.concat(shown)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
